import React, { useState, useEffect, useRef } from "react";
import { supplyChainService } from "../services/apiClient";
import { useToast } from "../contexts/ToastContext";

interface ProductCogItem {
  product_name: string;
  display_name: string;
  cogs_per_order: number;
  status: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

const ProductCogs: React.FC = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState<ProductCogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const fetchedRef = useRef(false);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await supplyChainService.getProductCogs();
      setProducts(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load product COGS";
      showToast("error", "Failed to load product COGS", msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadProducts();
  }, []);

  const handleEditClick = (product: ProductCogItem) => {
    setEditingProduct(product.product_name);
    setEditValue(product.cogs_per_order.toString());
  };

  const handleSave = async () => {
    if (!editingProduct) return;
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < 0) {
      showToast("error", "Enter a valid COGS value (0 or greater)");
      return;
    }
    try {
      await supplyChainService.updateProductCog(editingProduct, parsed);
      setProducts((prev) =>
        prev.map((p) =>
          p.product_name === editingProduct
            ? { ...p, cogs_per_order: parsed, status: "Set" }
            : p
        )
      );
      setEditingProduct(null);
      showToast("success", "COGS updated");
    } catch {
      showToast("error", "Failed to update COGS");
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setEditValue("");
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">How Product COGS Works</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>COGS (Cost of Goods Sold) is multiplied by the number of orders for CM$ calculation</li>
          <li>Products with "Not Set" status use $0.00 as their COGS value by default</li>
          <li>Set COGS for physical products that have production/fulfillment costs</li>
          <li>Digital products typically have $0.00 COGS</li>
        </ul>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-900 mb-2">Note</h3>
        <p className="text-sm text-amber-800">
          Products marked as "Not Set" will use $0.00 COGS in CM$ calculations. Configure COGS for products
          with actual production costs to ensure accurate CM$ values.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Product COGS ({products.length} products)
          </h2>
          <button
            onClick={() => {
              const rows = [["Product Name", "COGS per Order"]];
              products.forEach((p) => rows.push([p.display_name, p.cogs_per_order.toFixed(2)]));
              const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
              const a = document.createElement("a");
              a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
              a.download = "product_cogs.csv";
              a.click();
            }}
            disabled={products.length === 0}
            className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    COGS per Order
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.product_name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.display_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {editingProduct === product.product_name ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleSave}
                            className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        formatCurrency(product.cogs_per_order)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {editingProduct !== product.product_name && (
                        <button
                          onClick={() => handleEditClick(product)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit COGS"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCogs;
